import {ReactNode, useCallback, useRef, useState} from "react";
import chevronPrev from "./assets/images/chevron_prev.svg";
import { Swiper, SwiperRef, SwiperSlide } from "swiper/react";
import { Navigation, Pagination } from "swiper/modules";
import chevronNext from "./assets/images/chevron_next.svg";

type CarouselProps = {
  slides: ReactNode[];
};

export function Carousel({ slides }: CarouselProps) {
  const [hasNextSlide, setHasNextSlide] = useState<boolean>(true);
  const [hasPrevSlide, setHasPrevSlide] = useState<boolean>(true);
  const [allOnScreen, setAllOnScreen] = useState<boolean>(false);

  const updateSlideStatus = useCallback((swiper: SwiperRef['swiper']) => {
    setAllOnScreen(swiper.slides.length <= Number(swiper.params.slidesPerView));
    setHasNextSlide(swiper.params.loop || !swiper.isEnd);
    setHasPrevSlide(swiper.params.loop || !swiper.isBeginning);
  }, []);

  // for external next/prev buttons:
  const swiperRef = useRef<SwiperRef>(null);
  const handlePrev = useCallback(() => {
    if (swiperRef.current) {
      swiperRef.current.swiper.slidePrev();
      updateSlideStatus(swiperRef.current.swiper);
    }
  }, [updateSlideStatus]);
  const handleNext = useCallback(() => {
    if (swiperRef.current) {
      swiperRef.current.swiper.slideNext();
      updateSlideStatus(swiperRef.current.swiper);
    }
  }, [updateSlideStatus]);

  return (
    <div>
      <div className="relative w-[75%] sm:w-[85%] max-w-[1000px] mx-auto mb-8">
        { !allOnScreen && hasPrevSlide && <div
          onClick={handlePrev}
          className="absolute block w-14 h-18 cursor-pointer"
          style={{ left: "-60px", top: "50%", transform: "translateY(-50%)" }}
        >
          <img alt="Previous Slide" src={chevronPrev} className="w-14 h-18" />
        </div> }

        <Swiper
          // by providing a key, we can force the carousel to remount when the slides change
          // (avoid buggy behavior when the number of slides changes)
          key={slides.length}
          ref={swiperRef}
          modules={[Navigation, Pagination]}
          // spaceBetween={10}
          // slidesPerView={1}
          // slidesPerGroup={1}
          loop={true}
          onSlidesUpdated={(swiper) => {
            updateSlideStatus(swiper);
            // swiper.setProgress(0);
          }}
          breakpoints={{
            // when window width is >= 450px
            450: {
              slidesPerView: 2,
              slidesPerGroup: 2,
              spaceBetween: 10,
            },
            // when window width is >= 769px
            769: {
              slidesPerView: 4,
              slidesPerGroup: 4,
              spaceBetween: 10,
            },
          }}
        >
          {slides.map((slide, index) => (
            <SwiperSlide key={index} style={{ height: "100%" }}>
              {slide}
            </SwiperSlide>
          ))}
        </Swiper>

        {!allOnScreen && hasNextSlide && <div
          onClick={handleNext}
          className="absolute block w-14 h-18 cursor-pointer"
          style={{ right: "-60px", top: "50%", transform: "translateY(-50%)" }}
        >
          <img alt="Next Slide" src={chevronNext} className="w-14 h-18" />
        </div>}
      </div>
    </div>
  );
}
